import { z } from "zod";

/**
 * The Validator's run summary. Lives in `jobs.validatorResult.artifacts[0]`
 * once the orchestrator persists the validator's callback (see
 * `services/orchestrator/src/routes/callbacks.ts` § "validator path").
 *
 * Counters are service-tracked (incremented by the validator service as it
 * processes each tool call) — never self-reported by the LLM, which would
 * drift. The only free-form contribution from the model is `observations`,
 * populated via the `record_observation` tool.
 *
 * `scoredRows` carries the per-row outcome for each draft the run touched,
 * giving the run report a deterministic source of truth for the score
 * distribution and "suggested corrections" section. The fields here mirror
 * what the validator wrote to the sheet so a reader can tie the two
 * together without a second sheet read.
 *
 * `agentConfigSnapshot` is forensic provenance — it freezes the templating
 * values that drove this run so a row's `validator_flag` outcome is
 * interpretable later when the threshold may have moved.
 *
 * `promptVersion` is the git SHA of
 * `packages/prompts/src/db_curator_validator/default.md` at run time, or
 * `"dev"` when running from an uncommitted tree.
 */
export const validatorArtifactsSchema = z.object({
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime(),
  /** `null` = ran to completion. Any other value names what halted the run. */
  haltReason: z.string().nullable(),

  rowsValidated: z.number().int().nonnegative(),
  rowsFlagged: z.number().int().nonnegative(),
  rowsSkipped: z.number().int().nonnegative(),

  /**
   * Per-row outcome — one entry per draft the validator touched. Used by
   * the run report to render the score distribution and surface rows whose
   * `quality_notes` contain a "SUGGESTED CORRECTION:" prefix.
   */
  scoredRows: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string(),
      qualityScore: z.number(),
      validatorFlag: z.boolean(),
      hasSuggestedCorrection: z.boolean(),
    }),
  ),

  observations: z.array(z.string()),

  agentConfigSnapshot: z.record(z.union([z.string(), z.number()])),
  promptVersion: z.string().min(1),
});

export type ValidatorArtifacts = z.infer<typeof validatorArtifactsSchema>;
